import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import { getFilterFromLocalStorage, setFilterToLocalStorage } from "utils/common";
import { ENTITY_FILTER_TYPES } from "utils/common";
import NoDataFound from "components/noDataFound/NoDataFound";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import CustomTable from "components/table/CustomTable";
import { USER_USAGE_ATTEMPTS_API } from "constants/endpoints";
import { userUsageAttemptColumns } from "./variables";
import { formatDateToMMDDYYYY } from "utils/common";

const PAGE_SIZE = 50;
const today = new Date();
const thirtyDaysAgo = new Date();
thirtyDaysAgo.setDate(today.getDate() - 30);

const STATUS_OPTIONS = [
  { id: 1, label: "Rejected (limit hit)", value: "reject" },
  { id: 2, label: "Accepted", value: "accept" },
];

const ACTION_OPTIONS = [
  { id: 1, label: "View Number", value: "view_number" },
  { id: 2, label: "Share Number", value: "share_number" },
  { id: 3, label: "Lead View", value: "lead_view" },
  { id: 4, label: "Listings", value: "listings" },
  { id: 5, label: "Direct Lead", value: "direct_lead" },
];

const PLAN_TYPE_OPTIONS = [
  { id: 1, label: "Freemium", value: "freemium" },
  { id: 2, label: "Pro", value: "pro" },
  { id: 3, label: "Pro Plus", value: "pro_plus" },
];

export default function UserUsageAttempts() {
  const [data, setData] = useState([]);
  const { apiCall, loading = true, error } = useApi();
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const lsFilters = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.USER_USAGE_ATTEMPTS_FILTER
  );
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");

  const initialFiltersData = {
    status: "reject",
    action: "",
    plan_type: "",
    dateRange: {
      startDate: thirtyDaysAgo,
      endDate: today,
    },
  };

  const [filters, setFilters] = useState(
    lsFilters ? lsFilters : { ...initialFiltersData }
  );
  const {
    status = "reject",
    action = "",
    plan_type = "",
    dateRange: { startDate = "", endDate = "" } = {},
  } = filters || {};

  const fetchData = useCallback(
    async (providedText) => {
      try {
        const body = {
          search_text: providedText ?? searchText ?? "",
          page: currentPage,
          limit: pageSize,
          status,
          action,
          plan_type,
          start_date: formatDateToMMDDYYYY(startDate),
          end_date: formatDateToMMDDYYYY(endDate),
        };
        const res = await apiCall.get(USER_USAGE_ATTEMPTS_API, body);
        if (res?.statusCode === HttpStatusCode.Ok) {
          setData(res?.data?.attempts || []);
          setTotalPages(Math.ceil((res?.data?.count || 0) / pageSize));
          setTotalCount(res?.data?.count || 0);
        }
      } catch (err) {
        console.log("Error while fetching usage attempts:", err.message);
      }
    },
    [currentPage, pageSize, searchText, filters]
  );

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const toggleSidebar = () => setIsOpenFilterSidebar((v) => !v);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleApplyFilter = () => {
    const merged = {
      ...filters,
      dateRange: { startDate, endDate },
    };
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.USER_USAGE_ATTEMPTS_FILTER, merged);
    setCurrentPage(1);
    fetchData();
  };

  const handleRemoveAllFilter = () => {
    setFilters({
      ...initialFiltersData,
      dateRange: { startDate: "", endDate: "" },
    });
  };

  const setStartDate = (v) =>
    setFilters((p) => ({ ...p, dateRange: { ...p.dateRange, startDate: v } }));
  const setEndDate = (v) =>
    setFilters((p) => ({ ...p, dateRange: { ...p.dateRange, endDate: v } }));

  const dropdownFilters = useMemo(
    () => [
      {
        name: "status",
        placeholder: "Status",
        value: status,
        data: STATUS_OPTIONS,
      },
      {
        name: "action",
        placeholder: "Action",
        value: action,
        data: ACTION_OPTIONS,
      },
      {
        name: "plan_type",
        placeholder: "Plan Type",
        value: plan_type,
        data: PLAN_TYPE_OPTIONS,
      },
    ],
    [status, action, plan_type]
  );

  const allFiltersProps = { dropdowns: dropdownFilters };

  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData(searchText);
  };

  const columns = useMemo(() => userUsageAttemptColumns, []);
  const tableData = useMemo(() => data, [data]);

  if (error) return <ErrorDisplay error={error} />;

  return (
    <>
      <Box height="100vh" bg="#F8FAFB">
        <Flex
          justify="space-between"
          p={{ base: 2, md: 4 }}
          align="center"
          direction={{ base: "column", md: "row" }}
          boxShadow="sm"
          borderRadius="sm"
          pos={{ base: "relative", md: "fixed" }}
          w={{ base: "100%", md: "calc(100vw - 96px)" }}
          zIndex={1000}
          gap={{ base: 3, md: 4 }}
        >
          <HStack spacing={3}>
            <Box position="relative">
              <Button
                paddingX={6}
                leftIcon={<FiFilter />}
                rightIcon={<FiChevronsDown />}
                colorScheme="teal"
                variant="outline"
                borderRadius="md"
                size="sm"
                onClick={toggleSidebar}
              >
                Filter
              </Button>
            </Box>
            <form onSubmit={handleInputFormSubmit}>
              <InputGroup size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
          </HStack>
          <Flex gap={4} align="center">
            <Text
              fontSize="sm"
              borderRadius="5px"
              borderWidth="0.5px"
              borderColor="#637381"
              background="#F9F9FB"
              color="#637381"
              p={1}
            >
              Unique Attempts: {totalCount}
            </Text>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(value) => setCurrentPage(value)}
              totalCount={totalCount}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </Flex>
        </Flex>

        <SidebarFilter
          filters={allFiltersProps}
          handleFilterChange={handleFilterChange}
          handleApplyFilter={handleApplyFilter}
          isOpen={isOpenFilterSidebar}
          onClose={toggleSidebar}
          appliedFilters={filters}
          lsFilters={lsFilters}
          setFilters={setFilters}
          handleRemoveAllFilter={handleRemoveAllFilter}
          isCalenderFilter={true}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />

        {tableData?.length !== 0 ? (
          <CustomTable columns={columns} data={tableData} />
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        <OverlayLoaderComponent isOpen={loading} />
      </Box>
    </>
  );
}
